import {
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { r, s } from '@/theme/tokens';
import { colors } from '@/theme/tokens/colors';
import { alpha, Box, Popover } from '@/ui/primitives';
import { InfoOutlinedIcon } from '@/ui/primitives/icons';
import {
  buildMetricInfoPopoverPaperSx,
  metricInfoPopoverBodySx,
  type MetricInfoPopoverPlacement,
} from './chartPanelSurfaceSx';
import { buildPopoverFocusAreaMaskShadow } from './chartPanelSurfaceTokens';
import type { PopoverProps, SxProps, Theme } from '@/ui/primitives';

type MetricInfoPopoverButtonProps = {
  ariaLabel: string;
  children: ReactNode;
};

const metricTrendCardFrameSelector = '[data-metric-trend-card-frame="true"]';
const popoverWidthPx = 420;
const popoverViewportMarginPx = 32;
const popoverBackdropColor = alpha(colors.dark.bg, 0.5);
const metricInfoButtonSize = componentSizes.charts.metricInfoButtonSize;
const metricInfoIconSize = componentSizes.charts.metricInfoIconSize;
const popoverFocusAreaPadding = {
  top: 1,
  right: 1,
  bottom: 1,
  left: 2,
} as const;
const popoverFocusAreaShadowSpreadPx = 9999;

type FocusAreaRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const resolveAnchorOrigin = (
  placement: MetricInfoPopoverPlacement,
): PopoverProps['anchorOrigin'] => {
  if (placement === 'below') return { vertical: 'bottom', horizontal: 'center' };
  if (placement === 'right') return { vertical: 'top', horizontal: 'right' };
  return { vertical: 'top', horizontal: 'left' };
};

const resolveTransformOrigin = (
  placement: MetricInfoPopoverPlacement,
): PopoverProps['transformOrigin'] => {
  if (placement === 'below') return { vertical: 'top', horizontal: 'center' };
  if (placement === 'right') return { vertical: 'top', horizontal: 'left' };
  return { vertical: 'top', horizontal: 'right' };
};

const resolvePopoverPlacement = (anchorElement: HTMLElement): MetricInfoPopoverPlacement => {
  const anchorRect = anchorElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const popoverWidthWithMargin = popoverWidthPx + popoverViewportMarginPx;

  if (viewportWidth <= 600) return 'below';
  if (
    anchorRect.left < popoverWidthWithMargin &&
    viewportWidth - anchorRect.right >= popoverWidthWithMargin
  ) {
    return 'right';
  }
  return 'left';
};

const resolveFocusAreaRect = (element: HTMLElement): FocusAreaRect => {
  const rect = element.getBoundingClientRect();
  const paddedLeft = rect.left - popoverFocusAreaPadding.left;
  const paddedTop = rect.top - popoverFocusAreaPadding.top;
  const left = Math.max(0, paddedLeft);
  const top = Math.max(0, paddedTop);
  const right = Math.min(window.innerWidth, rect.right + popoverFocusAreaPadding.right);
  const bottom = Math.min(window.innerHeight, rect.bottom + popoverFocusAreaPadding.bottom);

  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
};

export const MetricInfoPopoverButton = ({
  ariaLabel,
  children,
}: MetricInfoPopoverButtonProps): ReactElement => {
  const popoverId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [focusAreaRect, setFocusAreaRect] = useState<FocusAreaRect | null>(null);
  const [popoverPlacement, setPopoverPlacement] = useState<MetricInfoPopoverPlacement>('left');
  const open = anchorEl !== null;

  const updateFocusAreaRect = useCallback((nextAnchorEl: HTMLElement): void => {
    setFocusAreaRect(resolveFocusAreaRect(nextAnchorEl));
  }, []);

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    const cardAnchor = event.currentTarget.closest(metricTrendCardFrameSelector);
    const nextAnchorEl = cardAnchor instanceof HTMLElement ? cardAnchor : event.currentTarget;
    setPopoverPlacement(resolvePopoverPlacement(nextAnchorEl));
    updateFocusAreaRect(nextAnchorEl);
    setAnchorEl(nextAnchorEl);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setFocusAreaRect(null);
  };

  useEffect(() => {
    if (!anchorEl) return undefined;
    const handleViewportChange = (): void => {
      updateFocusAreaRect(anchorEl);
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [anchorEl, updateFocusAreaRect]);

  const backdropSx: SxProps<Theme> = focusAreaRect
    ? {
        backgroundColor: 'transparent',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: `${String(focusAreaRect.top)}px`,
          left: `${String(focusAreaRect.left)}px`,
          width: `${String(focusAreaRect.width)}px`,
          height: `${String(focusAreaRect.height)}px`,
          borderRadius: r('lg'),
          ['boxShadow']: buildPopoverFocusAreaMaskShadow(
            popoverFocusAreaShadowSpreadPx,
            popoverBackdropColor,
          ),
          pointerEvents: 'none',
        },
      }
    : { backgroundColor: popoverBackdropColor };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={handleClick}
        sx={{
          appearance: 'none',
          border: 0,
          padding: s('none'),
          margin: s('none'),
          background: 'transparent',
          color: 'inherit',
          flexShrink: 0,
          width: metricInfoButtonSize,
          height: metricInfoButtonSize,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transform: 'translateY(-1px)',
          '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: s('xxs'),
            borderRadius: r('sm'),
          },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: metricInfoIconSize, color: 'inherit' }} />
      </Box>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={resolveAnchorOrigin(popoverPlacement)}
        transformOrigin={resolveTransformOrigin(popoverPlacement)}
        slotProps={{
          backdrop: {
            invisible: false,
            sx: backdropSx,
          },
          paper: { square: false },
        }}
        paperSx={buildMetricInfoPopoverPaperSx({
          placement: popoverPlacement,
          viewportMarginPx: popoverViewportMarginPx,
          widthPx: popoverWidthPx,
        })}
      >
        <Box sx={metricInfoPopoverBodySx}>{children}</Box>
      </Popover>
    </>
  );
};
